// Filter.js
import React, { useState, useCallback, useEffect } from 'react';
import {
  Dropdown,
  Form,
  Button,
  FormControl
} from 'react-bootstrap';
import InstagramSvg from '../assets/icons/Instagram_24px.svg';
import TikTokSvg from '../assets/icons/Tiktok_24px.svg';
import redHeart from '../assets/icons/red-heart-icon.svg';
import greyHeart from '../assets/icons/heart-thin-icon.svg';
import searchIcon from '../assets/icons/Search_24px.png';
import RemoveFilter from '../assets/icons/Close_24px.svg';
import '../styles/Filter.css';
import { nicheCategories } from '../utils/nicheCategory';

export default function Filter({ influencers, onFilter }) {
  // ─ State ─────────────────────────────────────────────────────────────
  const [minFollowers, setMinFollowers] = useState('');
  const [maxFollowers, setMaxFollowers] = useState('');
  const [gender, setGender] = useState('');
  const [nicheSearch, setNicheSearch] = useState('');
  const [nicheSuggestions, setNicheSuggestions] = useState([]);
  const [showNicheSuggestions, setShowNicheSuggestions] = useState(false);
  const [creatorSearch, setCreatorSearch] = useState('');
  // country: now search term + selected array
  const [countryTerm, setCountryTerm] = useState('');
  const [countrySuggestions, setCountrySuggestions] = useState([]);
  const [showCountrySuggestions, setShowCountrySuggestions] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);
  // language: same
  const [languageTerm, setLanguageTerm] = useState('');
  const [languageSuggestions, setLanguageSuggestions] = useState([]);
  const [showLanguageSuggestions, setShowLanguageSuggestions] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [platforms, setPlatforms] = useState({ Instagram: false, Tiktok: false });
  const [minRate, setMinRate] = useState('');
  const [maxRate, setMaxRate] = useState('');
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [hasPhone, setHasPhone] = useState('');
  const [favoriteFilter, setFavoriteFilter] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // derive full lists
  const allCountries = [...new Set(influencers.map(i => i.currentlyLiving).filter(Boolean))];
  const allLanguages = [...new Set(
    influencers.flatMap(i => (i.languages || '').split(',')).filter(Boolean)
  )];

  // ─ Close dropdowns on scroll ─────────────────────────────────────────
  useEffect(() => {
    const closeAll = () => setActiveDropdown(null);
    window.addEventListener('scroll', closeAll);
    return () => window.removeEventListener('scroll', closeAll);
  }, []);

  // ─ Close suggestions on outside click ────────────────────────────────
  useEffect(() => {
    const handler = e => {
      if (!e.target.closest('.niche-search-container')) setShowNicheSuggestions(false);
      if (!e.target.closest('.country-search-container')) setShowCountrySuggestions(false);
      if (!e.target.closest('.language-search-container')) setShowLanguageSuggestions(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const toggleDropdown = name => {
    const opening = activeDropdown !== name;
    setActiveDropdown(opening ? name : null);
    if (opening && name === 'country') {
      setCountrySuggestions(allCountries);
      setShowCountrySuggestions(true);
    }
    if (opening && name === 'language') {
      setLanguageSuggestions(allLanguages);
      setShowLanguageSuggestions(true);
    }
  };

  // ─ Core filter logic ─────────────────────────────────────────────────
  const applyFilter = useCallback(() => {
    const filtered = influencers.filter(inf => {
      const f = Number(inf.followers) || 0;
      const r = parseFloat((inf.rate || '').replace(/[^0-9.]/g, '')) || 0;
      const a = Number(inf.age) || 0;

      if (minFollowers && f < +minFollowers) return false;
      if (maxFollowers && f > +maxFollowers) return false;
      if (platforms.Instagram && !inf.instagramUsername) return false;
      if (platforms.Tiktok && !inf.tikTokUsername) return false;
      if (favoriteFilter !== null && inf.favorite !== favoriteFilter) return false;
      if (minRate && r < +minRate) return false;
      if (maxRate && r > +maxRate) return false;
      if (gender && inf.gender?.toLowerCase() !== gender.toLowerCase()) return false;
      if (minAge && a < +minAge) return false;
      if (maxAge && a > +maxAge) return false;
      if (hasPhone === 'yes' && !inf.phone) return false;
      if (hasPhone === 'no' && inf.phone) return false;
      // multi-country
      if (selectedCountries.length > 0 && !selectedCountries.includes(inf.currentlyLiving)) return false;
      // multi-language
      if (selectedLanguages.length > 0) {
        const langs = (inf.languages || '').split(',').map(l => l.trim());
        if (!selectedLanguages.some(sl => langs.includes(sl))) return false;
      }
      if (creatorSearch &&
          ![inf.creatorName, inf.instagramUsername, inf.tikTokUsername]
              .some(s => s?.toLowerCase().includes(creatorSearch.toLowerCase()))) return false;
      if (nicheSearch) {
        const terms = nicheSearch.split(',').map(t => t.trim().toLowerCase());
        if (!terms.every(t => inf.niches?.some(n => n.toLowerCase().includes(t)))) return false;
      }
      return true;
    });
    onFilter(filtered);
  }, [
    influencers,
    minFollowers, maxFollowers,
    platforms, favoriteFilter,
    minRate, maxRate,
    gender,
    minAge, maxAge,
    hasPhone,
    selectedCountries, selectedLanguages,
    creatorSearch, nicheSearch,
    onFilter
  ]);

  const resetFilter = () => {
    setMinFollowers(''); setMaxFollowers('');
    setPlatforms({ Instagram: false, Tiktok: false });
    setFavoriteFilter(null);
    setMinRate(''); setMaxRate('');
    setGender('');
    setMinAge(''); setMaxAge('');
    setHasPhone('');
    setCountryTerm(''); setSelectedCountries([]);
    setLanguageTerm(''); setSelectedLanguages([]);
    setCreatorSearch('');
    setNicheSearch('');
    onFilter(influencers);
  };

  const togglePlatform = p =>
    setPlatforms(prev => ({ ...prev, [p]: !prev[p] }));

  // ─ Suggestions handling ─────────────────────────────────────────────
  const handleNicheSearch = val => {
    setNicheSearch(val);
    if (!val) return setShowNicheSuggestions(false);
    const m = nicheCategories.filter(cat =>
      cat.category.toLowerCase().includes(val.toLowerCase()) ||
      cat.subcategories.some(s => s.toLowerCase().includes(val.toLowerCase()))
    );
    setNicheSuggestions(m);
    setShowNicheSuggestions(true);
  };

  const handleCountrySearch = val => {
    setCountryTerm(val);
    if (val === '' && activeDropdown === 'country') {
      setCountrySuggestions(allCountries);
      setShowCountrySuggestions(true);
      return;
    }
    if (val.length >= 1) {
      setCountrySuggestions(allCountries.filter(c =>
        c.toLowerCase().includes(val.toLowerCase()))
      );
      setShowCountrySuggestions(true);
    } else {
      setShowCountrySuggestions(false);
    }
  };

  const handleLanguageSearch = val => {
    setLanguageTerm(val);
    if (val === '' && activeDropdown === 'language') {
      setLanguageSuggestions(allLanguages);
      setShowLanguageSuggestions(true);
      return;
    }
    if (val.length >= 1) {
      setLanguageSuggestions(allLanguages.filter(l =>
        l.toLowerCase().includes(val.toLowerCase()))
      );
      setShowLanguageSuggestions(true);
    } else {
      setShowLanguageSuggestions(false);
    }
  };

  const pickNiche = s => {
    const arr = nicheSearch.split(',').slice(0, -1).concat(s).filter(Boolean);
    setNicheSearch(arr.join(',') + (nicheSearch.endsWith(',') ? ',' : ''));
    setShowNicheSuggestions(false);
  };

  // toggle country selection
  const pickCountry = c => {
    setSelectedCountries(prev =>
      prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]
    );
  };
  // toggle language selection
  const pickLanguage = l => {
    setSelectedLanguages(prev =>
      prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]
    );
  };

  // ─ Pills builder ────────────────────────────────────────────────────
  const pills = [];

  if (minFollowers || maxFollowers) {
    pills.push({ key: 'followers', label: `${minFollowers||0}-${maxFollowers||'∞'}`, clear: () => { setMinFollowers(''); setMaxFollowers(''); applyFilter(); } });
  }
  if (platforms.Instagram) pills.push({ key:'insta', label:'Instagram', clear:() => {togglePlatform('Instagram'); applyFilter()} });
  if (platforms.Tiktok)   pills.push({ key:'tt',   label:'TikTok',    clear:() => {togglePlatform('Tiktok');    applyFilter()} });
  if (favoriteFilter!==null) pills.push({ key:'fav', label: favoriteFilter?'Favorite':'Not Favorite', clear:() => { setFavoriteFilter(null); applyFilter(); } });
  if (minRate||maxRate) pills.push({ key:'rate', label:`${minRate||0}-${maxRate||'∞'}`, clear:() => { setMinRate(''); setMaxRate(''); applyFilter(); } });
  if (gender) pills.push({ key:'gender', label:gender, clear:() => { setGender(''); applyFilter(); } });
  if (minAge||maxAge) pills.push({ key:'age', label:`${minAge||0}-${maxAge||'∞'}`, clear:() => { setMinAge(''); setMaxAge(''); applyFilter(); } });
  if (hasPhone) pills.push({ key:'phone', label:hasPhone==='yes'?'Has Phone':'No Phone', clear:() => { setHasPhone(''); applyFilter(); } });
  // multi-country pills
  selectedCountries.forEach((c,i) =>
    pills.push({ key:`country-${i}`, label:c, clear:() => { setSelectedCountries(prev=>prev.filter(x=>x!==c)); applyFilter(); } })
  );
  // multi-language pills
  selectedLanguages.forEach((l,i) =>
    pills.push({ key:`lang-${i}`, label:l, clear:() => { setSelectedLanguages(prev=>prev.filter(x=>x!==l)); applyFilter(); } })
  );
  if (creatorSearch) pills.push({ key:'creator', label:creatorSearch, clear:() => { setCreatorSearch(''); applyFilter(); } });
  nicheSearch.split(',').map(s=>s.trim()).filter(Boolean).forEach((n,i)=>pills.push({
    key:`niche-${i}`, label:n, clear:()=>{ setNicheSearch(ns=>ns.split(',').map(t=>t.trim()).filter(t=>t!==n).join(',')); applyFilter(); }
  }));

  // ─ Render ────────────────────────────────────────────────────────────
  return (
    <div className="desktop-filter px-0">
      <div className="filter-container d-flex flex-column">

        {/* ─ Top row of dropdowns ─ */}
        <div className="filter-section d-flex flex-wrap align-items-center">

          {/* Followers */}
          <div className="dropdown-wrapper parallel-input me-2">
            <Dropdown show={activeDropdown==='followers'} onToggle={()=>toggleDropdown('followers')}>
              <Dropdown.Toggle className={`follower-toggle ${(minFollowers==='' && maxFollowers==='')?  '':'btn-slected'}`}>
                Followers
              </Dropdown.Toggle>
              <Dropdown.Menu className="follower-input">
                <div className='input-form'>
                <Form.Label>From</Form.Label>
                <Form.Control
                  type="number"
                  value={minFollowers}
                  onChange={e=>setMinFollowers(e.target.value)}
                  placeholder='From'
                />
                </div>
                <div className='input-form'>
                <Form.Label className="">To</Form.Label>
                <Form.Control
                  type="number"
                  value={maxFollowers}
                  onChange={e=>setMaxFollowers(e.target.value)}
                  placeholder='To'
                />
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Platform */}
          <div className="dropdown-wrapper me-2">
          <Dropdown>
      <Dropdown.Toggle className={`platform-toggle ${(platforms.Instagram || platforms.Tiktok)?  'btn-slected':''}`}>
        Platform
      </Dropdown.Toggle>

      <Dropdown.Menu as="div" className="p-3">
        <Dropdown.Header className="ps-0 drop-label">Filter Platform</Dropdown.Header>

        <Dropdown.Item as="div" className="ps-0">
  <label 
    className="d-flex align-items-center gap-1" 
    onClick={e => e.stopPropagation()}
  >
    <input
      type="checkbox"
      className="custom-checkbox"
      checked={platforms.Instagram}
      onChange={() => togglePlatform('Instagram')}
    />
    <img src={InstagramSvg} alt="Instagram" width={20} height={20} />
    Instagram
  </label>
</Dropdown.Item>

<Dropdown.Item as="div" className="ps-0">
  <label 
    className="d-flex align-items-center gap-1" 
    onClick={e => e.stopPropagation()}
  >
    <input
      type="checkbox"
      className="custom-checkbox"
      checked={platforms.Tiktok}
      onChange={() => togglePlatform('Tiktok')}
    />
    <img src={TikTokSvg} alt="TikTok" width={20} height={20} />
    TikTok
  </label>
</Dropdown.Item>

      </Dropdown.Menu>
    </Dropdown>
          </div>

          {/* Favorite */}
          <div className="dropdown-wrapper me-2">
            <Dropdown>
              <Dropdown.Toggle className={`follower-toggle ${(favoriteFilter !== null)?  'btn-slected':''}`}>
                Favorite
              </Dropdown.Toggle>
              <Dropdown.Menu className='ps-0'>
                <Dropdown.Item onClick={()=>setFavoriteFilter(null)}>
                  All
                </Dropdown.Item>
                <Dropdown.Item onClick={()=>setFavoriteFilter(true)} className='check-label'>
                  <img src={redHeart} width={16} alt="Fav" className='me-2'/> Favorite
                </Dropdown.Item>
                <Dropdown.Item onClick={()=>setFavoriteFilter(false)} className='check-label'>
                  <img src={greyHeart} width={17} alt="NotFav" className='me-2'/> Not Favorite
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Rate */}
          <div className="dropdown-wrapper parallel-input me-2">
            <Dropdown show={activeDropdown==='rate'} onToggle={()=>toggleDropdown('rate')}>
              <Dropdown.Toggle className={`follower-toggle ${(minRate==='' && maxRate==='')?  '':'btn-slected'}`}>
                Rate
              </Dropdown.Toggle>
              <Dropdown.Menu className="follower-input">
                <div>
                <Form.Label>Min</Form.Label>
                <Form.Control
                  type="number"
                  value={minRate}
                  onChange={e=>setMinRate(e.target.value)}
                  placeholder='Min'
                />
                </div>
                <div>
                <Form.Label className="">Max</Form.Label>
                <Form.Control
                  type="number"
                  value={maxRate}
                  onChange={e=>setMaxRate(e.target.value)}
                  placeholder='Max'
                />
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Gender */}
          <div className="dropdown-wrapper me-2">
            <Dropdown>
              <Dropdown.Toggle className={`follower-toggle ${gender?  'btn-slected':''}`}>
                {gender || 'Gender'}
              </Dropdown.Toggle>
              <Dropdown.Menu className='ps-0'>
              <Dropdown.Header className="drop-label">Gender</Dropdown.Header>
                {['All','Male','Female'].map(g => (
                  <Dropdown.Item
                    key={g}
                    onClick={() => setGender(g==='All'?'':g)}
                  >
                    {g}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Age */}
          <div className="dropdown-wrapper parallel-input me-2">
            <Dropdown show={activeDropdown==='age'} onToggle={()=>toggleDropdown('age')}>
              <Dropdown.Toggle className={`follower-toggle ${(minAge==='' && maxAge==='')?  '':'btn-slected'}`}>
                Age
              </Dropdown.Toggle>
              <Dropdown.Menu className="follower-input">
                <div>
                <Form.Label>Min</Form.Label>
                <Form.Control
                  type="number"
                  value={minAge}
                  onChange={e=>setMinAge(e.target.value)}
                  placeholder='Min'
                />
                </div>
                <div>
                <Form.Label className="">Max</Form.Label>
                <Form.Control
                  type="number"
                  value={maxAge}
                  onChange={e=>setMaxAge(e.target.value)}
                  placeholder='Max'
                />
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Phone */}
          <div className="dropdown-wrapper me-2">
            <Dropdown>
              <Dropdown.Toggle className={`follower-toggle ${hasPhone !==''?  'btn-slected':''}`}>
                Phone
              </Dropdown.Toggle>
              <Dropdown.Menu className='ps-0'>
              <Dropdown.Header className="drop-label">Phone Number</Dropdown.Header>
                {['All','Has Phone','No Phone'].map(opt=>(
                  <Dropdown.Item
                    key={opt}
                    onClick={()=>setHasPhone(
                      opt==='All' ? '' :
                      opt==='Has Phone' ? 'yes' : 'no'
                    )}
                  >
                    {opt}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Country */}
          <div className="dropdown-wrapper me-2 country-search-container">
            <Dropdown show={activeDropdown==='country'} onToggle={()=>toggleDropdown('country')}>
              <Dropdown.Toggle className={`follower-toggle ${selectedCountries.length?'btn-slected':''}`}>
                Country
              </Dropdown.Toggle>
              <Dropdown.Menu className="follower-input p-2">
                <Form.Label>Country</Form.Label>
                <FormControl
                  value={countryTerm}
                  onChange={e=>handleCountrySearch(e.target.value)}
                  placeholder="Filter…"
                  className="mb-2"
                />
                <div className="suggestions-dropdown-items">
                  {countrySuggestions.map((c,i)=>(
                    <label
                      key={i}
                      className="d-flex align-items-center gap-2 suggestions-dropdown-item"
                      onClick={e=>e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCountries.includes(c)}
                        onChange={()=>pickCountry(c)}
                        className='custom-checkbox w-auto d-none'
                      />
                      {c}
                    </label>
                  ))}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Languages */}
          <div className="dropdown-wrapper me-2 language-search-container">
            <Dropdown show={activeDropdown==='language'} onToggle={()=>toggleDropdown('language')}>
              <Dropdown.Toggle className={`follower-toggle ${selectedLanguages.length?'btn-slected':''}`}>
                Languages
              </Dropdown.Toggle>
              <Dropdown.Menu className="follower-input p-2">
                <Form.Label>Search Language</Form.Label>
                <FormControl
                  value={languageTerm}
                  onChange={e=>handleLanguageSearch(e.target.value)}
                  placeholder="Filter…"
                  className="mb-2"
                />
                <div className="suggestions-dropdown-items">
                  {languageSuggestions.map((l,i)=>(
                    <label
                      key={i}
                      className="d-flex align-items-center gap-2 suggestions-dropdown-item"
                      onClick={e=>e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedLanguages.includes(l)}
                        onChange={()=>pickLanguage(l)}
                        className='custom-checkbox w-auto'
                      />
                      {l}
                    </label>
                  ))}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* ─ Pills ─ */}
        <div>
          {pills.length > 0 && (
            <div className="applied-pills">
              {pills.map(p => (
                <div className="pill-content" key={p.key}>
                  <span className="pill">{p.label}</span>
                  <button className="pill-close" onClick={p.clear}>
                    <img src={RemoveFilter} alt="remove" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ─ Bottom row ─ */}
        <div className="filter-section mt-2 d-flex flex-wrap gap-2 align-items-center">
          {/* Niche */}
          <div className="position-relative niche-search-container" style={{ width: 260 }}>
            <img src={searchIcon} className="custom-search-icon" alt="Search" />
            <FormControl
              placeholder="Search Niches (Separate by comma)"
              value={nicheSearch}
              onChange={e => handleNicheSearch(e.target.value)}
              className="custom-search-input ps-5"
            />
            {showNicheSuggestions && (
              <div className="niche-suggestions">
                {nicheSuggestions.map((cat, i) => (
                  <React.Fragment key={i}>
                    <div
                      className="suggestion-header"
                      onClick={() => pickNiche(cat.category)}
                    >
                      {cat.category}
                    </div>
                    {cat.subcategories.map((sub, j) => (
                      <div
                        key={j}
                        className="suggestion-item"
                        onClick={() => pickNiche(sub)}
                      >
                        {sub}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Creator */}
          <div className="position-relative" style={{ width: 260 }}>
            <img src={searchIcon} className="custom-search-icon" alt="Search" />
            <FormControl
              placeholder="Search Creator (Separate by comma)"
              value={creatorSearch}
              onChange={e => setCreatorSearch(e.target.value)}
              className="custom-search-input ps-5"
            />
          </div>

          <Button
            onClick={applyFilter}
            style={{
              backgroundColor: '#875FFE',
              borderRadius: '20px',
              fontWeight: '600',
              padding: '10px 24px',
              fontSize: '14px',
              boxShadow: '0px 2px 0px 0px #6f4ad4',
              height: '40px'
            }}
          >
            Get Results
          </Button>
        </div>
      </div>
    </div>
  );
}
